<script setup>
import { computed, ref } from 'vue'
import { useCustomerStore } from "@/stores/CustomersStore"
import { useEventStore } from "@/stores/EventsStore"
import { useModalStore } from "@/stores/ModalStore"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { useSalesStore } from "@/stores/statistics/SalesStore"
import { useDisplay } from "vuetify"
import { useDebounceFn } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { extractDate } from "@/utils/datetime"
import emitter from '@/utils/emitter'
import { downloadVisitHistoryXls } from '@/utils/xls'
import VisitCard from "@/components/visits/VisitCard.vue"
import { mapStatsVisitToCard } from "@/components/visits/mapStatsVisit"
import { buildVisitSortingOptions } from "@/components/visits/visitSortingOptions"
import VisitAttachmentsButton from "@/components/visits/VisitAttachmentsButton.vue"
import TablePaginationComplete from "@/components/pagination/TablePaginationComplete.vue"
import CancelVisitDialog from "@/components/event/dialogs/CancelVisitDialog.vue"
import { useRoute, useRouter } from "vue-router"

const customerStore = useCustomerStore()
const modalStore = useModalStore()
const calendarStore = useCalendarStore()
const eventStore = useEventStore()
const salesStore = useSalesStore()

const { t } = useI18n()
const { mobile, smAndDown, width } = useDisplay()
const route = useRoute()

const options = customerStore.visitsHistoryOptions

const visitCards = computed(() => (customerStore.customerHistoryVisits || []).map(mapStatsVisitToCard))

// ========================================
// Fetching
// ========================================
const getVisits = async () => {
  const salonerClientId = customerStore.selectedCustomer?.salonerClientId
  if (!salonerClientId) return

  await customerStore.getVisitsAndSales(salonerClientId)
}

// Always back to page 1 — the current page may not exist under the new filters
const reload = async () => {
  options.page = 1
  await getVisits()
}

const onEmployeeSelect = async (employeeId) => {
  options.filters.employeeId = String(employeeId)
  await reload()
}

const debouncedSearch = useDebounceFn(() => {
  // Backend requires at least 2 characters; clearing the field (null or '') drops the filter
  const term = options.search || ''
  if (term.length >= 2 || term === '') reload()
}, 500)

// ========================================
// Filters
// ========================================
const employeeOptions = computed(() => {
  const allOption = {
    title: t('show_all'),
    value: null,
  }

  const employees = calendarStore.employeesCalendars.map(emp => ({
    title: emp.fullName || emp.title,
    value: String(emp.employeeId),
  }))

  const unavailableEmployees = calendarStore.unavailableEmployees
    .filter(emp => !employees.some(e => e.value === String(emp.employeeId)))
    .map(emp => ({
      title: emp.fullName || emp.title,
      value: String(emp.employeeId),
    }))

  return [allOption, ...employees, ...unavailableEmployees]
})

// Deleted visits are never part of the client history, so that status is not offered here
const statusOptions = computed(() => [
  { title: t('visits_stats_tab.status_finished'), value: '1' },
  { title: t('visits_stats_tab.status_confirmed'), value: '2' },
  { title: t('visits_stats_tab.status_canceled'), value: '3' },
  { title: t('visits_stats_tab.status_confirmed_by_client'), value: '7' },
  { title: t('visits_stats_tab.status_client_in_salon'), value: '8' },
])

const visitSourceOptions = computed(() => [
  { title: t('visits_stats_tab.visit_source_panel'), value: '1' },
  { title: t('visits_stats_tab.visit_source_portal'), value: '2' },
  { title: t('visits_stats_tab.visit_source_app'), value: '5' },
])

// Shared with /statistics/visits - see buildVisitSortingOptions().
// Sorting by client is left out: the history always belongs to a single client.
const sortingOptions = computed(() => buildVisitSortingOptions(t))

const activeFiltersCount = computed(() => [
  options.filters.status,
  options.filters.visitSource,
  options.filters.employeeId,
].filter(Boolean).length)

const hasActiveFilters = computed(() => activeFiltersCount.value > 0)

// Search counts as a filter for the clear button, but stays out of the mobile badge:
// it sits in the toolbar, not behind the filters toggle
const canClearFilters = computed(() => hasActiveFilters.value || Boolean(options.search))

// On mobile the filters live behind a toggle, expanded right below the toolbar
const showMobileFilters = ref(false)

// The toolbar only fits one line with full labels in a wide modal — below that the actions drop their text
const compactActions = computed(() => width.value < 1600)

// Status and source only exist for visits, so filtering by either hides product sales
const salesHiddenByFilters = computed(() =>
  Boolean(options.filters.status) || Boolean(options.filters.visitSource))

const clearFilters = async () => {
  customerStore.clearVisitsHistoryFilters()
  await getVisits()
}

// ========================================
// Pagination
// ========================================
const onPageUpdate = async (page) => {
  options.page = page
  await getVisits()
}

const onPerPageUpdate = async (perPage) => {
  options.perPage = perPage
  await reload()
}

// ========================================
// Actions
// ========================================
const cancelVisit = async (visit) => {
  try {
    const result = await modalStore.openModal('cancelEvent', '', false, {
      eventId: visit.id,
      selectedCustomer: eventStore.selectedCustomer,
      otherCyclicalVisits: [],
      refetchEventsOnCalendar: true,
      closeAllModals: false,
    })

    if (result.success) {
      if (String(visit.id) === String(eventStore.selectedEvent?.eventId)) {
        eventStore.allowResetEvent = false
        modalStore.closeModal('calendarEvent')
      }
      await getVisits()
    }
  } catch (e) {
  }
}

const isDeletingSale = ref({})

const deleteSale = async (visit) => {
  const confirmationText = "w dn. " + extractDate(new Date(visit.date)) + " na kwotę " + visit.paid + " " + calendarStore.currency

  const confirmed = await modalStore.openConfirmationModal(confirmationText, t('sales.confirm_deletion'))
  if (!confirmed) return

  isDeletingSale.value[visit.id] = true
  try {
    const response = await customerStore.deleteProductSale(visit.id)
    if (response.success) await getVisits()
  } catch (e) {
    console.error('Error deleting selling:', e)
  } finally {
    isDeletingSale.value[visit.id] = false
  }
}

// displayElementOnCalendar() only reacts to elements of type 'events'
const showOnCalendar = (visit) => {
  calendarStore.displayElementOnCalendar({ id: visit.id, type: 'events' }, visit.date)
}

// Visits that can still be cancelled — finished, cancelled and no-show ones cannot
const canCancelVisit = visit =>
  visit.kind !== 'sale' &&
  !['1', '3', '5', '6'].includes(String(visit.status?.id)) &&
  !calendarStore.viewSettings.calendarReadOnly

// ========================================
// Export
// ========================================
const isDownloadingXls = ref(false)

const downloadXLS = async () => {
  const { name, surname, salonerClientId } = customerStore.selectedCustomer || {}
  if (!salonerClientId) return

  isDownloadingXls.value = true
  try {
    // Same params as the list, so the spreadsheet matches what is on screen
    await downloadVisitHistoryXls(
      customerStore.buildVisitsHistoryParams(salonerClientId),
      name,
      surname,
    )
  } finally {
    isDownloadingXls.value = false
  }
}

onMounted(async () => {
  if (calendarStore.employeesCalendars.length === 0) {
    await calendarStore.fetchEmployeesAndResources()
  }

  await getVisits()
  emitter.on('sale-changed', getVisits) // Refetch the history after any change in the sale dialog
})

onBeforeUnmount(() => {
  customerStore.visitsHistoryOptions.page = 1
  customerStore.visitsHistoryOptions.search = ''
  emitter.off('sale-changed', getVisits)
})
</script>

<template>
  <VSheet id="customer-history-visits">
    <!--
      Toolbar: search, filters, sorting and actions in one row.
      order-md-* holds the desktop sequence, while mobile follows the DOM order:
      search + actions, then the filters toggle with sorting, then the filters themselves.
    -->
    <!-- justify-end only bites on wrapped lines — the search field eats all the slack on the first one -->
    <!-- Wider row-gap keeps the floating labels off the fields above once the toolbar wraps -->
    <div class="d-flex flex-wrap justify-end align-center gc-2 gr-4 mt-2 mb-4">
      <VTextField
        v-model="options.search"
        class="order-md-1 flex-grow-1"
        :min-width="mobile ? 120 : 200"
        :width="mobile ? 170 : undefined"
        density="compact"
        hide-details="auto"
        :label="$t('search')"
        color="secondary"
        prepend-inner-icon="ri-search-line"
        clearable
        @update:model-value="debouncedSearch"
      />
      <!-- Mobile-only filters toggle -->
      <VBtn
        v-if="mobile"
        variant="outlined"
        :color="hasActiveFilters ? 'primary' : 'secondary'"
        style="min-height: 40px;"
        @click="showMobileFilters = !showMobileFilters"
      >
        <template #prepend>
          <VIcon
            size="18"
            icon="ri-filter-3-line"
          />
        </template>
        {{ $t('filters') }}
        <VBadge
          v-if="hasActiveFilters"
          :content="activeFiltersCount"
          color="primary"
          inline
        />
      </VBtn>

      <!-- Actions stay one flex item, so the dividers never get left behind on wrap -->
      <div class="order-md-6 d-flex align-center ga-2">
        <VDivider
          v-if="!mobile"
          vertical
          class="mx-1"
        />
        <VBtn
          variant="outlined"
          color="secondary"
          :disabled="customerStore.isLoading"
          :loading="isDownloadingXls"
          @click="downloadXLS"
        >
          <span v-if="!compactActions">
            {{ $t('download') }}
          </span>
          <template #prepend>
            <VIcon
              icon="ri-file-excel-2-line"
              size="16"
              :class="[compactActions ? 'mr-n2' : '']"
            />
          </template>
        </VBtn>
        <VBtn
          variant="outlined"
          color="secondary"
          :disabled="customerStore.isLoading"
          @click="getVisits"
        >
          <span v-if="!compactActions">
            {{ $t('refresh') }}
          </span>
          <template #prepend>
            <VIcon
              icon="ri-reset-left-line"
              :class="[compactActions ? 'mr-n2' : '']"
            />
          </template>
        </VBtn>
        <VDivider
          v-if="!mobile"
          vertical
          class="mx-1"
        />
      </div>

      <VSelect
        v-model="options.sortBy"
        class="order-md-7 flex-grow-0"
        :width="mobile ? 170 : 230"
        min-width="0"
        :disabled="customerStore.isLoading"
        density="compact"
        :label="t('sorting')"
        color="secondary"
        :items="sortingOptions"
        variant="outlined"
        @update:model-value="() => reload()"
      >
        <template #item="{ props: itemProps, item }">
          <VListItem v-bind="itemProps">
            <template #prepend>
              <VIcon :icon="item.raw.icon" />
            </template>
          </VListItem>
        </template>
      </VSelect>

      <!-- Filters — inline on desktop, collapsed behind the toggle on mobile -->
      <template v-if="!mobile || showMobileFilters">
        <VAutocomplete
          v-model="options.filters.employeeId"
          class="order-md-2 flex-grow-0"
          :width="mobile ? '100%' : 210"
          min-width="0"
          :label="t('employee')"
          color="secondary"
          :items="employeeOptions"
          autocomplete="off"
          :clearable="typeof options.filters.employeeId === 'string' || typeof options.filters.employeeId === 'number'"
          item-title="title"
          item-value="value"
          variant="outlined"
          density="compact"
          @update:model-value="() => reload()"
        />
        <VSelect
          v-model="options.filters.status"
          class="order-md-3 flex-grow-0"
          :width="mobile ? '100%' : 170"
          min-width="0"
          :label="t('status')"
          color="secondary"
          :items="statusOptions"
          item-title="title"
          item-value="value"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="() => reload()"
        />
        <VSelect
          v-model="options.filters.visitSource"
          class="order-md-4 flex-grow-0"
          :width="mobile ? '100%' : 200"
          min-width="0"
          :label="t('visits_stats_tab.visit_source')"
          color="secondary"
          :items="visitSourceOptions"
          item-title="title"
          item-value="value"
          variant="outlined"
          density="compact"
          clearable
          @update:model-value="() => reload()"
        />
        <VBtn
          variant="outlined"
          :disabled="!canClearFilters || customerStore.isLoading"
          :color="canClearFilters ? 'primary' : 'gray'"
          class="order-md-5 px-2"
          :class="mobile ? 'w-100' : ''"
          style="min-height: 40px;"
          @click="clearFilters"
        >
          <template #prepend>
            <VIcon
              size="18"
              icon="ri-filter-off-line"
              :class="[compactActions && !mobile ? 'mr-n2' : '']"
            />
          </template>
          <span v-if="!compactActions || mobile">
            {{ $t('clear_filters') }}
          </span>
        </VBtn>
      </template>
    </div>

    <VAlert
      v-if="salesHiddenByFilters"
      type="info"
      variant="tonal"
      density="compact"
      class="mb-4 text-body-2"
    >
      {{ $t('visits_history_tab.sales_hidden_by_filters') }}
    </VAlert>

    <VSkeletonLoader
      v-if="customerStore.isLoading"
      type="list-item-two-line@3"
    />

    <template v-else>
      <div
        v-if="visitCards.length"
        class="ga-3"
        :style="{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr)'
        }"
      >
        <VisitCard
          v-for="visit in visitCards"
          :key="`${visit.kind}-${visit.id}`"
          :visit="visit"
          clickable-employee
          :show-real-visit-duration="calendarStore.viewSettings.showRealEventDuration"
          @employee-click="onEmployeeSelect($event.id)"
        >
          <template #actions>
            <div
              class="d-flex justify-end w-100 ga-1"
              :class="{'flex-wrap':smAndDown}"
            >
              <VisitAttachmentsButton
                :attachments="visit.attachments"
                :class="{ 'flex-fill' : mobile}"
                :add-label="mobile"
              >
                <template #text>
                  <span class="ml-1">{{ t('attachments') }}</span>
                </template>
              </VisitAttachmentsButton>
              <!-- Cancel visit -->
              <VBtn
                v-if="canCancelVisit(visit)"
                size="small"
                variant="flat"
                color="error"
                :class="{ 'flex-fill' : mobile}"
                :title="t('revoke_visit_tooltip')"
                @click="cancelVisit(visit)"
              >
                <template #prepend>
                  <VIcon
                    size="16"
                    class="mr-n2"
                    icon="ri-calendar-close-fill"
                  />
                </template>
                <span
                  v-if="mobile"
                  class="mx-1"
                >
                  {{ t('revoke_visit_tooltip') }}
                </span>
                <VTooltip
                  activator="parent"
                  open-on-click
                >
                  {{ t('revoke_visit_tooltip') }}
                </VTooltip>
              </VBtn>

              <!-- Delete sale -->
              <VBtn
                v-if="visit.kind === 'sale' && calendarStore.viewSettings.allowDeleteVisit && !calendarStore.viewSettings.calendarReadOnly"
                size="small"
                variant="flat"
                color="error"
                :class="{ 'flex-fill' : mobile}"
                :loading="isDeletingSale[visit.id]"
                :title="t('delete')"
                @click="deleteSale(visit)"
              >
                <template #prepend>
                  <VIcon
                    size="16"
                    class="mr-n2"
                    icon="ri-delete-bin-line"
                  />
                </template>
                <span
                  v-if="mobile"
                  class="mx-1"
                >
                  {{ t('delete') }}
                </span>
                <VTooltip
                  activator="parent"
                  open-on-click
                >
                  {{ t('delete') }}
                </VTooltip>
              </VBtn>

              <!-- Go to sale -->
              <VBtn
                v-if="visit.kind === 'sale'"
                size="small"
                variant="flat"
                color="secondary"
                :class="{ 'flex-fill' : mobile}"
                :title="t('actions.proceed_to_sales')"
                @click="salesStore.handleSaleClick(visit.id, false, false)"
              >
                <template #prepend>
                  <VIcon
                    size="16"
                    class="mr-n2"
                    icon="ri-shopping-cart-line"
                  />
                </template>
                <span
                  v-if="mobile"
                  class="mx-1"
                >
                  {{ t('actions.proceed_to_sales') }}
                </span>
                <VTooltip
                  activator="parent"
                  open-on-click
                >
                  {{ t('actions.proceed_to_sales') }}
                </VTooltip>
              </VBtn>

              <!-- Show on calendar -->
              <VBtn
                v-else
                size="small"
                variant="flat"
                color="secondary"
                :class="{ 'flex-fill' : mobile}"
                :title="t('showOnCalendar')"
                @click="showOnCalendar(visit)"
              >
                <template #prepend>
                  <VIcon
                    size="16"
                    class="mr-n2"
                    icon="ri-calendar-line"
                  />
                </template>
                <span
                  v-if="mobile"
                  class="mx-1"
                >
                  {{ t('showOnCalendar') }}
                </span>
                <VTooltip
                  activator="parent"
                  open-on-click
                >
                  {{ t('showOnCalendar') }}
                </VTooltip>
              </VBtn>
            </div>
          </template>
        </VisitCard>
      </div>
      <div
        v-else
        class="pa-8 text-center text-disabled"
      >
        {{ t('no_matching_results') }}
      </div>
    </template>

    <TablePaginationComplete
      v-if="visitCards.length"
      v-model="options.page"
      v-model:per-page="options.perPage"
      class="my-4"
      :length="Math.ceil(options.total / options.perPage)"
      :page-size-items="[5, 10, 15, 20]"
      show-results-summary
      :filtered-count="options.total"
      :total-count="options.total"
      :what="t('clients_stats.visits')"
      @update:model-value="onPageUpdate"
      @update:per-page="onPerPageUpdate"
    />


    <!-- 👉 Cancel visit modal -->
    <CancelVisitDialog
      v-if="modalStore.modals.cancelEvent && route.path !=='/calendar'"
      :selected-customer="eventStore.selectedCustomer"
    />
  </VSheet>
</template>

<style lang="scss">
#customer-history-visits {
  .v-data-table-headers--mobile {
    display: none;
  }
}
</style>
